    import { useState, useEffect, useRef } from "react";
    import ReactDOM from 'react-dom';
    import DatePicker from "src/components/common/calendar/DatePicker";
    import { formatLocalDate } from "src/utils/date/dateUtils";

    interface DateRangeInputProps {
    startDate?: string; // YYYY-MM-DD 또는 ISO 형식
    endDate?: string; // YYYY-MM-DD 또는 ISO 형식
    onStartDateChange: (date: string) => void; // YYYY-MM-DD 형식으로 전달
    onEndDateChange: (date: string) => void; // YYYY-MM-DD 형식으로 전달
    label?: string;
    labelWidth?: string;
    className?: string;
    }

    const DateRangeInput = ({
    startDate,
    endDate,
    onStartDateChange,
    onEndDateChange,
    label = "기간",
    labelWidth = "w-20",
    className = "",
    }: DateRangeInputProps) => {
    // 달력 표시 여부 및 선택 모드 (시작일 또는 종료일)
    const [datePickerVisible, setDatePickerVisible] = useState(false);
    const [datePickerMode, setDatePickerMode] = useState<'start' | 'end'>('start');
    const [pickerPosition, setPickerPosition] = useState({ top: 0, left: 0, height: 0 });
    const dateInputRef = useRef<HTMLInputElement>(null);
    const datePickerContainerRef = useRef<HTMLDivElement>(null);

    // 날짜 문자열을 Date 객체로 변환
    const getDateFromString = (dateStr: string | undefined): Date | undefined => {
        if (!dateStr) return undefined;
        const dateOnly = dateStr.includes('T') ? dateStr.split('T')[0] : dateStr;
        const date = new Date(dateOnly);
        return isNaN(date.getTime()) ? undefined : date;
    };

    const startDateObj = getDateFromString(startDate);
    const endDateObj = getDateFromString(endDate);

    // 기간 표시 텍스트 (하나의 필드에 표시)
    const periodDisplayText = (): string => {
        if (startDateObj && endDateObj) {
        return `${formatLocalDate(startDateObj)} ~ ${formatLocalDate(endDateObj)}`;
        } else if (startDateObj) {
        return formatLocalDate(startDateObj);
        }
        return "";
    };

    // 달력 위치 계산
    useEffect(() => {
        if (datePickerVisible && dateInputRef.current) {
        const rect = dateInputRef.current.getBoundingClientRect();
        setPickerPosition({
            top: rect.bottom + window.scrollY + 4,
            left: rect.left + window.scrollX,
            height: rect.height,
        });
        }
    }, [datePickerVisible]);

    // 달력 표시 시 포커스
    useEffect(() => {
        if (datePickerVisible && datePickerContainerRef.current) {
        datePickerContainerRef.current.focus();
        }
    }, [datePickerVisible]);

    // 기간 입력 필드 클릭
    const handlePeriodClick = () => {
        // 시작일이 없으면 시작일 모드, 있으면 종료일 모드
        if (!startDateObj) {
        setDatePickerMode('start');
        } else if (!endDateObj) {
        setDatePickerMode('end');
        } else {
        // 둘 다 있으면 시작일부터 다시 선택
        setDatePickerMode('start');
        }
        setDatePickerVisible(true);
    };

    // 날짜 선택 처리 (날짜 클릭 시)
    const handleDateChange = (date: Date) => {
        const dateStr = formatLocalDate(date);
        if (datePickerMode === 'start') {
        onStartDateChange(dateStr);
        } else {
        onEndDateChange(dateStr);
        }
    };

    // 날짜 확인 처리 (더블클릭 또는 확인 버튼)
    const handleDateConfirm = (date: Date) => {
        const dateStr = formatLocalDate(date);
        if (datePickerMode === 'start') {
        onStartDateChange(dateStr);
        // 시작일 선택 완료 후 종료일 선택 모드로 전환
        setDatePickerMode('end');
        // 달력은 계속 표시 (종료일 선택 대기)
        } else {
        onEndDateChange(dateStr);
        setDatePickerVisible(false);
        }
    };

    // 날짜 초기화
    const handleDateReset = () => {
        const today = new Date();
        const todayStr = formatLocalDate(today);
        if (datePickerMode === 'start') {
        onStartDateChange(todayStr);
        setDatePickerMode('end');
        // 달력은 계속 표시 (종료일 선택 대기)
        } else {
        onEndDateChange(todayStr);
        setDatePickerVisible(false);
        }
    };

    // 날짜 클리어
    const handleDateClear = () => {
        if (datePickerMode === 'start') {
        onStartDateChange("");
        } else {
        onEndDateChange("");
        }
        setDatePickerVisible(false);
    };

    return (
        <div className={`flex items-center gap-1 ${className}`}>
        <label className={`text-lg font-medium text-black ${labelWidth} flex-shrink-0`}>
            {label}
        </label>
        <div className="flex-1 relative">
            <input
            ref={dateInputRef}
            type="text"
            value={periodDisplayText()}
            readOnly
            onClick={handlePeriodClick}
            className="w-full max-w-[270px] px-2 py-0.5 text-lg border border-gray-300 rounded bg-white text-black focus:outline-none focus:ring-2 focus:ring-blue-500 cursor-pointer"
            placeholder="연도-월-일 ~ 연도-월-일"
            />
            {datePickerVisible && ReactDOM.createPortal(
            <>
                <div
                className="fixed top-0 left-0 w-screen h-screen z-[99998]"
                onClick={() => setDatePickerVisible(false)}
                />
                <div
                ref={datePickerContainerRef}
                style={{
                    position: 'fixed',
                    top: `${pickerPosition.top}px`,
                    left: `${pickerPosition.left}px`,
                    zIndex: 99999,
                }}
                onClick={(e) => e.stopPropagation()}
                tabIndex={0}
                >
                <DatePicker
                    value={datePickerMode === 'start' ? startDateObj : endDateObj}
                    onChange={handleDateChange}
                    onReset={handleDateReset}
                    onClear={handleDateClear}
                    onConfirm={handleDateConfirm}
                    yearMonthVisible={false}
                    timeEditable={false}
                />
                </div>
            </>,
            document.body
            )}
        </div>
        </div>
    );
    };

    export default DateRangeInput;

